import React, { useState, useEffect, useRef, useCallback } from 'react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Search, Cpu, Plus, Eye, Handshake, ChevronDown, ChevronRight,
  X, Clock, Bookmark, BookmarkPlus, Loader2,
  FileText, Sparkles, FolderTree, SlidersHorizontal, UserPlus,
  AlertTriangle, Zap, CircuitBoard, MemoryStick, Plug, Radio,
  Gauge, Lightbulb, Box,
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import QuickAssignModal from '@/components/QuickAssignModal';
import InfoTip from '@/components/InfoTip';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/services/api';
import { useCurrency } from '@/context/CurrencyContext';

const riskColor = (r) => r === 'High' ? 'bg-red-100 text-red-800' : r === 'Medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800';

// Icons for top-level categories
const catIcons = {
  active_components: Cpu,
  passive_components: CircuitBoard,
  electromechanical: Gauge,
  interconnects: Plug,
  others: Box,
};

/* ── Category Tree Node ── */
const TreeNode = ({ node, level = 0, selectedId, onSelect }) => {
  const [open, setOpen] = useState(level === 0);
  const hasChildren = node.children?.length > 0;
  const isSelected = selectedId === node.id;
  const isParentOfSelected = hasChildren && node.children.some(c =>
    c.id === selectedId || (c.children && c.children.some(gc => gc.id === selectedId))
  );

  // Auto-expand when child is selected
  useEffect(() => {
    if (isParentOfSelected && !open) setOpen(true);
  }, [isParentOfSelected]); // eslint-disable-line

  return (
    <div>
      <button
        className={`w-full flex items-center gap-1.5 py-1.5 rounded-md text-left text-[13px] transition-all
          ${isSelected ? 'bg-green-100 text-green-800 font-semibold' : 'hover:bg-gray-100 text-gray-700'}
          ${level === 0 ? 'font-medium text-gray-900' : ''}`}
        style={{ paddingLeft: `${level * 14 + 8}px`, paddingRight: '8px' }}
        onClick={() => { if (hasChildren) setOpen(!open); onSelect(node.id, node.name); }}
        data-testid={`tree-node-${node.id}`}
      >
        {hasChildren ? (open ? <ChevronDown className="h-3 w-3 text-gray-400 flex-shrink-0" /> : <ChevronRight className="h-3 w-3 text-gray-400 flex-shrink-0" />) : <span className="w-3 flex-shrink-0" />}
        <span className="truncate flex-1">{node.name}</span>
        {node.count > 0 && <span className="text-[10px] text-gray-400 font-normal ml-1 flex-shrink-0">{node.count}</span>}
      </button>
      {open && hasChildren && node.children.map(child => (
        <TreeNode key={child.id} node={child} level={level + 1} selectedId={selectedId} onSelect={onSelect} />
      ))}
    </div>
  );
};

const ComponentAnalysis = () => {
  const navigate = useNavigate();
  const { format } = useCurrency();
  const [searchParams] = useSearchParams();

  // Category tree
  const [categories, setCategories] = useState([]);
  const [selectedCat, setSelectedCat] = useState('');
  const [selectedCatName, setSelectedCatName] = useState('');
  const [catLoading, setCatLoading] = useState(true);

  // Parametric filters
  const [paramFilters, setParamFilters] = useState([]);
  const [activeFilters, setActiveFilters] = useState({});
  const [mfrFilter, setMfrFilter] = useState('');
  const [lifecycleFilter, setLifecycleFilter] = useState('');

  // Search state
  const [query, setQuery] = useState(searchParams.get('mpn') || '');
  const [suggestions, setSuggestions] = useState({ mpns: [], manufacturers: [], categories: [] });
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [results, setResults] = useState([]);
  const [totalResults, setTotalResults] = useState(0);
  const [availableMfrs, setAvailableMfrs] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);
  const filterDebounceRef = useRef(null);

  // Recent searches & watchlist
  const [recentSearches, setRecentSearches] = useState([]);
  const [watchlist, setWatchlist] = useState([]);

  // Modals
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignCtx, setAssignCtx] = useState({});
  const [sourceModal, setSourceModal] = useState(null);
  const [sourceQty, setSourceQty] = useState('');
  const [sourceNotes, setSourceNotes] = useState('');
  const [sourceSubmitting, setSourceSubmitting] = useState(false);

  // Show/hide tree panel
  const [showTree, setShowTree] = useState(true);

  // Parametric search function (stable ref)
  const doParametricSearch = useCallback(async (opts = {}) => {
    const catId = opts.catId !== undefined ? opts.catId : selectedCat;
    const textQ = opts.textQ !== undefined ? opts.textQ : query;
    const filters = opts.filters !== undefined ? opts.filters : activeFilters;
    const mfr = opts.mfr !== undefined ? opts.mfr : mfrFilter;
    const lc = opts.lc !== undefined ? opts.lc : lifecycleFilter;

    setShowSuggestions(false);
    setSearchLoading(true);
    setHasSearched(true);
    try {
      const res = await api.post('/components/parametric-search', {
        category_id: catId,
        text_query: textQ,
        filters,
        manufacturer: mfr,
        lifecycle: lc,
      });
      setResults(res.data.results || []);
      setTotalResults(res.data.total || 0);
      setAvailableMfrs(res.data.manufacturers || []);
      if (textQ) api.post('/components/search-history', { mpn: textQ }).catch(() => {});
    } catch { toast.error('Search failed'); }
    finally { setSearchLoading(false); }
  }, [selectedCat, query, activeFilters, mfrFilter, lifecycleFilter]);

  // Fetch categories, history, watchlist on mount
  useEffect(() => {
    const init = async () => {
      try {
        const [catRes, histRes, watchRes] = await Promise.allSettled([
          api.get('/components/categories'),
          api.get('/components/search-history'),
          api.get('/components/watchlist'),
        ]);
        if (catRes.status === 'fulfilled') setCategories(catRes.value.data.categories || []);
        if (histRes.status === 'fulfilled') setRecentSearches(histRes.value.data.history || []);
        if (watchRes.status === 'fulfilled') setWatchlist(watchRes.value.data.watchlist || []);
      } catch { /* ignore */ }
      finally { setCatLoading(false); }
    };
    init();
  }, []);

  // Auto-search if ?mpn= is set
  useEffect(() => {
    const mpn = searchParams.get('mpn');
    if (mpn) { setQuery(mpn); doParametricSearch({ textQ: mpn }); }
  }, []); // eslint-disable-line

  // Load parametric filters when category changes + auto-search
  useEffect(() => {
    if (!selectedCat) { setParamFilters([]); return; }
    const fetchAndSearch = async () => {
      try {
        const res = await api.get(`/components/parametric-filters/${selectedCat}`);
        setParamFilters(res.data.filters || []);
      } catch { setParamFilters([]); }
      // Auto-search when category selected
      doParametricSearch({ catId: selectedCat, filters: {} });
      setActiveFilters({});
    };
    fetchAndSearch();
  }, [selectedCat]); // eslint-disable-line

  // Auto-search when parametric filters change (debounced)
  useEffect(() => {
    if (!hasSearched) return;
    if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
    filterDebounceRef.current = setTimeout(() => {
      doParametricSearch();
    }, 400);
    return () => { if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current); };
  }, [activeFilters, mfrFilter, lifecycleFilter]); // eslint-disable-line

  // Predictive search
  const handleQueryChange = (val) => {
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (val.length < 2) { setSuggestions({ mpns: [], manufacturers: [], categories: [] }); setShowSuggestions(false); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await api.get(`/components/search/predictive?q=${encodeURIComponent(val)}`);
        setSuggestions(res.data);
        setShowSuggestions(true);
      } catch { /* ignore */ }
    }, 300);
  };

  // Close suggestions on click outside
  useEffect(() => {
    const handler = (e) => { if (searchRef.current && !searchRef.current.contains(e.target)) setShowSuggestions(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleCategorySelect = (id, name) => {
    if (id === selectedCat) {
      setSelectedCat('');
      setSelectedCatName('');
      setParamFilters([]);
      setActiveFilters({});
      setResults([]);
      setHasSearched(false);
    } else {
      setSelectedCat(id);
      setSelectedCatName(name || id);
      setMfrFilter('');
      setLifecycleFilter('');
    }
  };

  // Request / Source / Watch handlers
  const isWatched = (mpn) => watchlist.some(w => w.mpn === mpn);

  const handleSourceRequest = async () => {
    if (!sourceModal) return;
    setSourceSubmitting(true);
    try {
      const res = await api.post('/components/sourcing-request', { mpn: sourceModal, quantity: parseInt(sourceQty) || 0, notes: sourceNotes });
      toast.success(`Sourcing request ${res.data.reference} created`);
      setSourceModal(null); setSourceQty(''); setSourceNotes('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSourceSubmitting(false); }
  };
  const toggleWatch = async (mpn) => {
    try {
      if (isWatched(mpn)) {
        await api.delete(`/components/watchlist/${encodeURIComponent(mpn)}`);
        setWatchlist(prev => prev.filter(w => w.mpn !== mpn));
        toast.success('Removed from watchlist');
      } else {
        await api.post('/components/watchlist', { mpn, threshold: 5 });
        setWatchlist(prev => [...prev, { mpn, threshold: 5 }]);
        toast.success('Added to watchlist');
      }
    } catch { toast.error('Failed'); }
  };

  const clearAllFilters = () => {
    setSelectedCat('');
    setSelectedCatName('');
    setActiveFilters({});
    setMfrFilter('');
    setLifecycleFilter('');
    setParamFilters([]);
    setQuery('');
    setResults([]);
    setHasSearched(false);
  };

  const hasSuggestions = suggestions.mpns.length > 0 || suggestions.manufacturers.length > 0 || suggestions.categories.length > 0;
  const activeFilterCount = Object.values(activeFilters).filter(Boolean).length + (mfrFilter ? 1 : 0) + (lifecycleFilter ? 1 : 0) + (selectedCat ? 1 : 0);

  return (
    <Layout title="Part Intelligence" subtitle="Search, analyze, and track components" breadcrumbs={[{ label: 'Part Intelligence' }]}>
      <div className="space-y-4" data-testid="component-analysis-page">

        {/* Search Bar */}
        <div className="flex gap-2">
          <div className="flex-1 relative" ref={searchRef}>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  value={query} onChange={e => handleQueryChange(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') doParametricSearch(); }}
                  onFocus={() => { if (hasSuggestions) setShowSuggestions(true); }}
                  placeholder="Search by MPN, keyword, or description..."
                  className="pl-10 h-10 text-sm border-gray-300 focus:border-green-500 focus:ring-green-500" data-testid="component-search-input"
                />
              </div>
              <Button onClick={() => doParametricSearch()} disabled={searchLoading} className="bg-green-600 hover:bg-green-700 text-white h-10 px-4 text-sm" data-testid="component-search-btn">
                {searchLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4 mr-1" />} Search
              </Button>
            </div>

            {/* Suggestions Dropdown */}
            {showSuggestions && hasSuggestions && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-80 overflow-y-auto" data-testid="search-suggestions">
                {suggestions.mpns.length > 0 && (
                  <div className="p-2">
                    <p className="text-[10px] text-gray-400 uppercase font-bold px-2 mb-1">Components</p>
                    {suggestions.mpns.map((m, i) => (
                      <button key={i} className="w-full text-left px-3 py-2 rounded-md hover:bg-green-50 flex items-center gap-3 transition-colors" onClick={() => { setShowSuggestions(false); navigate(`/component-intelligence/${encodeURIComponent(m.mpn)}`); }} data-testid={`suggestion-mpn-${i}`}>
                        <Cpu className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                        <span className="font-mono font-semibold text-sm flex-1">{m.mpn}</span>
                        <span className="text-xs text-gray-500">{m.manufacturer}</span>
                        <Badge variant="outline" className="text-[10px]">{m.category}</Badge>
                      </button>
                    ))}
                  </div>
                )}
                {suggestions.manufacturers.length > 0 && (
                  <div className="p-2 border-t">
                    <p className="text-[10px] text-gray-400 uppercase font-bold px-2 mb-1">Manufacturers</p>
                    {suggestions.manufacturers.map((m, i) => (
                      <button key={i} className="w-full text-left px-3 py-2 rounded-md hover:bg-blue-50 flex items-center gap-2 text-sm" onClick={() => { setQuery(m); doParametricSearch({ textQ: m }); setShowSuggestions(false); }}>
                        <FileText className="h-3 w-3 text-blue-400" /> {m}
                      </button>
                    ))}
                  </div>
                )}
                {suggestions.categories.length > 0 && (
                  <div className="p-2 border-t">
                    <p className="text-[10px] text-gray-400 uppercase font-bold px-2 mb-1">Categories</p>
                    {suggestions.categories.map((c, i) => (
                      <button key={i} className="w-full text-left px-3 py-2 rounded-md hover:bg-purple-50 flex items-center gap-2 text-sm" onClick={() => { setQuery(c); doParametricSearch({ textQ: c }); setShowSuggestions(false); }}>
                        <Sparkles className="h-3 w-3 text-purple-400" /> {c}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <Button variant="outline" onClick={() => setShowTree(!showTree)} className={`h-10 text-sm ${showTree ? 'bg-green-50 border-green-300 text-green-700' : ''}`} data-testid="toggle-tree-btn">
            <FolderTree className="h-4 w-4 mr-1" /> {showTree ? 'Hide' : 'Show'} Tree
          </Button>
        </div>

        {/* Active Filters Strip */}
        {activeFilterCount > 0 && (
          <div className="flex items-center gap-2 flex-wrap px-1" data-testid="active-filters">
            <span className="text-xs text-gray-500 font-medium">Filters:</span>
            {selectedCatName && <Badge className="bg-green-100 text-green-800 gap-1 text-xs font-normal">{selectedCatName} <X className="h-3 w-3 cursor-pointer" onClick={() => handleCategorySelect(selectedCat)} /></Badge>}
            {mfrFilter && <Badge className="bg-blue-100 text-blue-800 gap-1 text-xs font-normal">{mfrFilter} <X className="h-3 w-3 cursor-pointer" onClick={() => setMfrFilter('')} /></Badge>}
            {lifecycleFilter && <Badge className="bg-purple-100 text-purple-800 gap-1 text-xs font-normal">{lifecycleFilter} <X className="h-3 w-3 cursor-pointer" onClick={() => setLifecycleFilter('')} /></Badge>}
            {Object.entries(activeFilters).filter(([,v]) => v).map(([k, v]) => (
              <Badge key={k} className="bg-gray-100 text-gray-700 gap-1 text-xs font-normal">{k}: {v} <X className="h-3 w-3 cursor-pointer" onClick={() => setActiveFilters(prev => ({...prev, [k]: ''}))} /></Badge>
            ))}
            <button className="text-xs text-red-500 hover:text-red-700 ml-1" onClick={clearAllFilters}>Clear all</button>
          </div>
        )}

        {/* Main Content: Tree Sidebar + Results */}
        <div className="flex gap-4">
          {/* Category Tree Panel */}
          {showTree && (
            <div className="w-56 flex-shrink-0" data-testid="category-tree-panel">
              <Card className="bg-white border-gray-200 shadow-sm sticky top-4">
                <CardHeader className="py-2.5 px-3 border-b bg-gray-50">
                  <CardTitle className="text-xs flex items-center gap-1.5 text-gray-600 uppercase tracking-wide">
                    <FolderTree className="h-3.5 w-3.5 text-green-600" /> Browse Categories
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-1.5 max-h-[calc(100vh-280px)] overflow-y-auto">
                  {catLoading ? (
                    <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>
                  ) : (
                    <div className="space-y-0">
                      {categories.map(cat => (
                        <TreeNode key={cat.id} node={cat} selectedId={selectedCat} onSelect={handleCategorySelect} />
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Right: Filters + Results */}
          <div className="flex-1 min-w-0 space-y-3">

            {/* Parametric Filters Bar — inline, compact */}
            {paramFilters.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm" data-testid="parametric-filters">
                <div className="flex items-center gap-2 mb-2">
                  <SlidersHorizontal className="h-3.5 w-3.5 text-green-600" />
                  <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Narrow by attributes</span>
                  {searchLoading && <Loader2 className="h-3 w-3 animate-spin text-gray-400" />}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                  {paramFilters.map(f => (
                    <div key={f.key}>
                      <Label className="text-[10px] text-gray-500 uppercase">{f.label}</Label>
                      <Select value={activeFilters[f.key] || ''} onValueChange={v => setActiveFilters(prev => ({...prev, [f.key]: v === '__all__' ? '' : v}))}>
                        <SelectTrigger className="h-7 mt-0.5 text-xs"><SelectValue placeholder="Any" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__all__">Any</SelectItem>
                          {f.options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                  {availableMfrs.length > 1 && (
                    <div>
                      <Label className="text-[10px] text-gray-500 uppercase">Manufacturer</Label>
                      <Select value={mfrFilter || ''} onValueChange={v => setMfrFilter(v === '__all__' ? '' : v)}>
                        <SelectTrigger className="h-7 mt-0.5 text-xs"><SelectValue placeholder="Any" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__all__">Any</SelectItem>
                          {availableMfrs.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div>
                    <Label className="text-[10px] text-gray-500 uppercase">Lifecycle</Label>
                    <Select value={lifecycleFilter || ''} onValueChange={v => setLifecycleFilter(v === '__all__' ? '' : v)}>
                      <SelectTrigger className="h-7 mt-0.5 text-xs"><SelectValue placeholder="Any" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__all__">Any</SelectItem>
                        <SelectItem value="Active">Active</SelectItem>
                        <SelectItem value="NRND">NRND</SelectItem>
                        <SelectItem value="Obsolete">Obsolete</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}

            {/* ── EMPTY STATE: Quick Browse + Recent/Watchlist ── */}
            {!hasSearched && (
              <div className="space-y-4">
                {/* Quick Browse Grid */}
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Quick browse — select a category to see components instantly</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                    {(categories || []).map(cat => {
                      const Icon = catIcons[cat.id] || Box;
                      return (
                        <button
                          key={cat.id}
                          onClick={() => {
                            // Find first child with components
                            const firstChild = cat.children?.find(c => c.count > 0) || cat.children?.[0];
                            if (firstChild) handleCategorySelect(firstChild.id, firstChild.name);
                          }}
                          className="flex flex-col items-center gap-1.5 p-3 bg-white border border-gray-200 rounded-lg hover:border-green-400 hover:bg-green-50 transition-all text-center group"
                          data-testid={`quick-browse-${cat.id}`}
                        >
                          <Icon className="h-5 w-5 text-gray-400 group-hover:text-green-600 transition-colors" />
                          <span className="text-xs font-medium text-gray-700 group-hover:text-green-800">{cat.name}</span>
                          <span className="text-[10px] text-gray-400">{cat.count} parts</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* Recent Searches */}
                  <Card className="bg-white border-gray-200 shadow-sm">
                    <CardHeader className="py-2.5 px-4 border-b"><CardTitle className="text-xs flex items-center gap-1.5 text-gray-600"><Clock className="h-3.5 w-3.5" /> Recent Searches</CardTitle></CardHeader>
                    <CardContent className="p-2">
                      {recentSearches.length === 0 ? (
                        <p className="text-xs text-gray-400 py-3 text-center">No recent searches yet</p>
                      ) : (
                        <div className="space-y-0.5">
                          {recentSearches.slice(0, 8).map((h, i) => (
                            <button key={i} onClick={() => navigate(`/component-intelligence/${encodeURIComponent(h.mpn)}`)} className="w-full text-left px-2.5 py-1.5 rounded hover:bg-gray-50 flex items-center gap-2 text-xs transition-colors" data-testid={`recent-search-${i}`}>
                              <Search className="h-3 w-3 text-gray-300" />
                              <span className="font-mono font-medium text-gray-800">{h.mpn}</span>
                              <span className="text-[10px] text-gray-400 ml-auto">{h.searched_at ? new Date(h.searched_at).toLocaleDateString() : ''}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Watchlist */}
                  <Card className="bg-white border-gray-200 shadow-sm">
                    <CardHeader className="py-2.5 px-4 border-b"><CardTitle className="text-xs flex items-center gap-1.5 text-gray-600"><Bookmark className="h-3.5 w-3.5 text-amber-500" /> Watchlist</CardTitle></CardHeader>
                    <CardContent className="p-2">
                      {watchlist.length === 0 ? (
                        <p className="text-xs text-gray-400 py-3 text-center">No components watched yet</p>
                      ) : (
                        <div className="space-y-0.5">
                          {watchlist.map((w, i) => (
                            <div key={i} className="flex items-center gap-2 px-2.5 py-1.5 rounded hover:bg-gray-50" data-testid={`watchlist-item-${i}`}>
                              <Bookmark className="h-3 w-3 text-amber-500 fill-amber-500 flex-shrink-0" />
                              <button onClick={() => navigate(`/component-intelligence/${encodeURIComponent(w.mpn)}`)} className="font-mono font-medium text-xs hover:text-green-700 flex-1 text-left">{w.mpn}</button>
                              <Badge variant="outline" className="text-[9px] h-4">{'\u00B1'}{w.threshold}%</Badge>
                              <button className="text-gray-300 hover:text-red-500" onClick={() => toggleWatch(w.mpn)}><X className="h-3 w-3" /></button>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

              </div>
            )}

            {/* ── SEARCH RESULTS ── */}
            {hasSearched && (
              <Card className="bg-white border-gray-200 shadow-sm" data-testid="search-results">
                <CardHeader className="py-2.5 px-4 border-b flex-row items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-xs text-gray-600">{totalResults} result{totalResults !== 1 ? 's' : ''}</CardTitle>
                    {selectedCatName && <Badge variant="outline" className="text-[10px] h-4">{selectedCatName}</Badge>}
                    {searchLoading && <Loader2 className="h-3 w-3 animate-spin text-gray-400" />}
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  {results.length === 0 ? (
                    <div className="text-center py-10 px-4">
                      <Search className="h-10 w-10 text-gray-200 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">No components match these filters.</p>
                      <p className="text-xs text-gray-400 mt-1">Try broadening your search or selecting a different category.</p>
                      <Button variant="outline" size="sm" className="mt-3 text-xs" onClick={clearAllFilters}>Clear Filters</Button>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-50 text-xs">
                            <TableHead className="font-semibold">MPN</TableHead>
                            <TableHead className="font-semibold">Description</TableHead>
                            <TableHead className="font-semibold">Manufacturer</TableHead>
                            <TableHead className="font-semibold">Price</TableHead>
                            <TableHead className="font-semibold">Median</TableHead>
                            <TableHead className="font-semibold">Best Price</TableHead>
                            <TableHead className="font-semibold">Lifecycle</TableHead>
                            <TableHead className="font-semibold">Risk</TableHead>
                            <TableHead className="font-semibold">YTEOL</TableHead>
                            <TableHead className="font-semibold">Lead Time</TableHead>
                            <TableHead className="font-semibold">Single Src</TableHead>
                            <TableHead className="text-right font-semibold">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {results.map((comp, idx) => (
                            <TableRow key={idx} className="hover:bg-green-50/50 cursor-pointer transition-colors" onClick={() => navigate(`/component-intelligence/${encodeURIComponent(comp.mpn)}`)} data-testid={`comp-result-${idx}`}>
                              <TableCell className="font-mono font-semibold text-xs text-green-800">{comp.mpn}</TableCell>
                              <TableCell className="text-xs text-gray-500 max-w-[180px] truncate">{comp.description}</TableCell>
                              <TableCell className="text-xs">{comp.manufacturer}</TableCell>
                              <TableCell className="text-xs font-medium">{format(comp.price)}</TableCell>
                              <TableCell className="text-xs text-blue-700">{format(comp.median)}</TableCell>
                              <TableCell className="text-xs text-green-700 font-medium">{format(comp.best_rate)}</TableCell>
                              <TableCell><Badge className={`text-[10px] h-4 ${comp.lifecycle === 'Active' ? 'bg-green-100 text-green-800' : comp.lifecycle === 'NRND' ? 'bg-orange-100 text-orange-800' : 'bg-red-100 text-red-800'}`}>{comp.lifecycle}</Badge></TableCell>
                              <TableCell><Badge className={`text-[10px] h-4 ${riskColor(comp.risk_level)}`}>{comp.risk_level}</Badge></TableCell>
                              <TableCell className="text-xs">{comp.yteol ? `${comp.yteol}mo` : '-'}</TableCell>
                              <TableCell className="text-xs">{comp.lead_time_days ? `${comp.lead_time_days}d` : '-'}</TableCell>
                              <TableCell>{comp.single_source ? <Badge className="bg-red-100 text-red-800 text-[10px] h-4">Yes</Badge> : <Badge className="bg-green-100 text-green-800 text-[10px] h-4">No</Badge>}</TableCell>
                              <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                                <div className="flex items-center gap-0.5 justify-end">
                                  <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => toggleWatch(comp.mpn)} data-testid={`watch-toggle-${idx}`}>
                                    {isWatched(comp.mpn) ? <Bookmark className="h-3 w-3 text-amber-500 fill-amber-500" /> : <BookmarkPlus className="h-3 w-3 text-gray-400" />}
                                  </Button>
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0"><ChevronDown className="h-3 w-3 text-gray-400" /></Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-44">
                                      <DropdownMenuItem onClick={() => navigate(`/component-intelligence/${encodeURIComponent(comp.mpn)}`)} className="text-xs"><Eye className="h-3 w-3 mr-2" /> View Detail</DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => setSourceModal(comp.mpn)} className="text-xs"><Handshake className="h-3 w-3 mr-2 text-green-600" /> Refer to 1Source</DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => { setAssignCtx({ mpn: comp.mpn, sourceModule: 'Part Intelligence' }); setAssignOpen(true); }} className="text-xs"><UserPlus className="h-3 w-3 mr-2 text-blue-600" /> Assign</DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      <QuickAssignModal open={assignOpen} onClose={() => setAssignOpen(false)} context={assignCtx} />

      <Dialog open={!!sourceModal} onOpenChange={() => setSourceModal(null)}>
        <DialogContent className="max-w-md" data-testid="source-request-modal">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-base"><Handshake className="h-4 w-4 text-green-600" /> Refer to 1Source</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="p-2.5 bg-gray-50 rounded-lg border"><p className="text-[10px] text-gray-500 uppercase">Component</p><p className="font-mono font-semibold text-sm">{sourceModal}</p></div>
            <div><Label className="text-xs">Quantity</Label><Input type="number" value={sourceQty} onChange={e => setSourceQty(e.target.value)} placeholder="Required quantity" className="mt-1 h-8" /></div>
            <div><Label className="text-xs">Notes</Label><Input value={sourceNotes} onChange={e => setSourceNotes(e.target.value)} placeholder="Special requirements..." className="mt-1 h-8" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setSourceModal(null)}>Cancel</Button>
            <Button size="sm" onClick={handleSourceRequest} disabled={sourceSubmitting} className="bg-green-600 hover:bg-green-700 text-white" data-testid="submit-source-request">{sourceSubmitting ? 'Submitting...' : 'Submit'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </Layout>
  );
};

export default ComponentAnalysis;
